import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { SubmitVersionPage } from '../../../../../../components/tasks/submit-version-page';
import { EmptyState } from '../../../../../../components/ui/empty-state';
import { PageSkeleton } from '../../../../../../components/ui/skeleton';
import { Forbidden, LoadFailure } from '../../../../../../components/ui/state-views';
import { pillClass } from '../../../../../../components/ui/toolbar-controls';
import { taskQuery, versionsQuery } from '../../../../../../lib/tasks-api';

export const Route = createFileRoute('/subdomain/admin/_staff/tasks/$taskId/submit')({
  // The work card and the notes column: the same wide canvas as the task page.
  staticData: { wide: true },
  component: SubmitRoute,
});

/** The statuses a version can be started in: the task is being worked, or the team is answering the client's revision. */
const WORKING = ['In Progress', 'Revision Requested'];

function SubmitRoute() {
  const { taskId } = Route.useParams();
  const task = useQuery(taskQuery(taskId));
  const versions = useQuery({ ...versionsQuery(taskId), enabled: !!task.data });
  const back = (
    <Link to="/subdomain/admin/tasks/$taskId" params={{ taskId }} className={pillClass('light', 'lg')}>
      Back to task
    </Link>
  );

  if (task.isLoading || (task.data && versions.isLoading)) return <PageSkeleton />;

  if (!task.data || !versions.data) {
    return (
      <LoadFailure
        error={task.error}
        noun="task"
        forbiddenHint="Ask an admin to assign you to it."
        onRetry={() => {
          void task.refetch();
          void versions.refetch();
        }}
      />
    );
  }

  if (!task.data.access.work || !WORKING.includes(task.data.status)) {
    return (
      <div className="rounded-(--mbm-component-radius-card) border border-border-line bg-surface-card">
        {task.data.access.work ? (
          <EmptyState
            title="This task can’t take a version right now"
            description="A version can be sent while the task is in progress, or when the client asked for changes."
            action={back}
          />
        ) : (
          <Forbidden
            title="Only the people on this task can submit a version"
            description="Ask the account manager or an admin to put you on it."
            action={back}
          />
        )}
      </div>
    );
  }

  return <SubmitVersionPage task={task.data} versions={versions.data} />;
}
